/**
 * Sentry Configuration
 * 
 * Production error tracking and performance monitoring.
 * Only initializes in production to avoid noise during development.
 */

import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry
 * Call this once in your app entry point (main.tsx)
 */
export function initSentry() {
  // Only initialize in production
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      
      // Set the environment
      environment: import.meta.env.VITE_APP_ENV || 'production',
      
      // Release tracking (set via build process)
      release: import.meta.env.VITE_APP_VERSION,
      
      // Performance monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Privacy: Mask all text and block all media by default
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      
      // 10% of transactions: enough to see slow pages without paying for every navigation.
      tracesSampleRate: 0.1,
      
      // Capture 10% of sessions for replay
      // Only capture replays for errors
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      
      // Filter out sensitive data
      beforeSend(event) {
        // Remove sensitive headers
        if (event.request?.headers) {
          delete event.request.headers['Authorization']
          delete event.request.headers['Cookie']
          delete event.request.headers['X-CSRF-Token']
        }
        
        // Filter out local development errors
        if (event.request?.url?.includes('localhost')) {
          return null
        }
        
        return event
      },
      
      // Don't send personally identifiable information
      beforeBreadcrumb(breadcrumb) {
        // Filter out sensitive breadcrumbs
        if (breadcrumb.category === 'console') {
          return null
        }
        
        return breadcrumb
      },
      
      // Ignore certain errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        // Network errors (usually not actionable)
        'Network request failed',
        'NetworkError',
        // User cancelled requests
        'AbortError',
        'cancelled',
      ],
      
      // Deny URLs (don't capture errors from these sources)
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
      ],
    })
    
    console.log('✅ Sentry initialized for error tracking')
  } else {
    console.log('ℹ️  Sentry disabled (development mode or no DSN configured)')
  }
}

/**
 * Set user context for error tracking
 * Call this after successful login
 */
export function setSentryUser(user: {
  id: string
  email: string
  organizationId: string
}) {
  if (import.meta.env.PROD) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      organizationId: user.organizationId,
    })
  }
}

/**
 * Clear user context
 * Call this after logout
 */
export function clearSentryUser() {
  if (import.meta.env.PROD) {
    Sentry.setUser(null)
  }
}

/** A page stuck in a loop must not flood the endpoint, so each page load reports a few at most. */
const MAX_BACKEND_REPORTS_PER_LOAD = 5
let backendReports = 0

/**
 * Tell our own server, which alerts when these spike. Works with or without a Sentry DSN. The
 * message only: no context, since it can hold customer details. Must never throw.
 */
function reportToBackend(error: Error) {
  if (backendReports >= MAX_BACKEND_REPORTS_PER_LOAD) return
  backendReports++
  try {
    const body = JSON.stringify({ message: error.message, path: window.location.pathname })
    navigator.sendBeacon?.('/api/client-errors', new Blob([body], { type: 'application/json' }))
  } catch {
    // reporting an error must never cause one
  }
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      extra: context,
    })
    reportToBackend(error)
  } else {
    console.error('Error:', error, context)
  }
}

/**
 * Capture a message (for non-error events)
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, level)
  } else {
    console.log(`[${level.toUpperCase()}]`, message)
  }
}

/**
 * Add breadcrumb (for debugging context)
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  if (import.meta.env.PROD) {
    Sentry.addBreadcrumb({
      message,
      data,
      level: 'info',
    })
  }
}

/**
 * Create an error boundary component
 * Use this to wrap your app or critical sections
 */
export const ErrorBoundary = Sentry.ErrorBoundary

/**
 * Create a profiler to track component performance
 */
export const Profiler = Sentry.Profiler
